import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiTag, FiPercent,
  FiDollarSign, FiCalendar, FiClock, FiCheckCircle, FiAlertCircle,
  FiImage, FiUpload
} from 'react-icons/fi';
import styles from './AdminSales.module.css';

const AdminSales = () => {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: '',
    discount_type: 'percentage',
    discount_value: '',
    start_date: '',
    end_date: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSales();
    fetchProducts();
  }, []);

  const fetchSales = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('sales')
      .select('*')
      .order('created_at', { ascending: false });

    if (data && !error) {
      // Fetch products for each sale
      const salesWithProducts = await Promise.all(
        data.map(async (sale) => {
          const { data: saleProducts } = await supabase
            .from('sale_products')
            .select('product_id')
            .eq('sale_id', sale.id);
          return {
            ...sale,
            products: saleProducts?.map(p => p.product_id) || []
          };
        })
      );
      setSales(salesWithProducts);
    }
    setLoading(false);
  };

  const fetchProducts = async () => {
    const { data, error } = await supabase
      .from('products')
      .select('id, name, price, image_url')
      .order('name');
    if (data && !error) setProducts(data);
  };

  const uploadImage = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `sale_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `sales/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('product-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
      return;
    }

    setUploading(true);
    const imageUrl = await uploadImage(file);
    if (imageUrl) {
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
    } else {
      setMessage({ type: 'error', text: 'Failed to upload image' });
    }
    setUploading(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const handleOpenModal = (sale = null) => {
    if (sale) {
      setEditingSale(sale);
      setFormData({
        name: sale.name,
        slug: sale.slug,
        description: sale.description || '',
        image_url: sale.image_url || '',
        discount_type: sale.discount_type,
        discount_value: sale.discount_value,
        start_date: sale.start_date ? sale.start_date.split('T')[0] : '',
        end_date: sale.end_date ? sale.end_date.split('T')[0] : '',
        is_active: sale.is_active,
      });
      setSelectedProductIds(sale.products || []);
    } else {
      setEditingSale(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        image_url: '',
        discount_type: 'percentage',
        discount_value: '',
        start_date: '',
        end_date: '',
        is_active: true,
      });
      setSelectedProductIds([]);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.discount_value || !formData.start_date || !formData.end_date) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }

    setSaving(true);
    const data = {
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
      image_url: formData.image_url,
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      start_date: formData.start_date,
      end_date: formData.end_date,
      is_active: formData.is_active,
      updated_at: new Date(),
    };

    let result;
    if (editingSale) {
      result = await supabase
        .from('sales')
        .update(data)
        .eq('id', editingSale.id);
      const saleId = editingSale.id;
      // Update sale_products
      if (result.error) {
        setMessage({ type: 'error', text: 'Failed to update sale' });
        setSaving(false);
        return;
      }
      // Delete existing products
      await supabase.from('sale_products').delete().eq('sale_id', saleId);
      // Insert new products
      if (selectedProductIds.length > 0) {
        const insertData = selectedProductIds.map(pid => ({ sale_id: saleId, product_id: pid }));
        await supabase.from('sale_products').insert(insertData);
      }
    } else {
      result = await supabase
        .from('sales')
        .insert([{ ...data, created_at: new Date() }])
        .select()
        .single();
      if (!result.error && result.data) {
        const saleId = result.data.id;
        if (selectedProductIds.length > 0) {
          const insertData = selectedProductIds.map(pid => ({ sale_id: saleId, product_id: pid }));
          await supabase.from('sale_products').insert(insertData);
        }
      }
    }

    if (result.error) {
      setMessage({ type: 'error', text: 'Failed to save sale: ' + result.error.message });
    } else {
      setMessage({ type: 'success', text: `Sale ${editingSale ? 'updated' : 'created'} successfully!` });
      fetchSales();
      setShowModal(false);
    }
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) return;
    const { error } = await supabase
      .from('sales')
      .delete()
      .eq('id', id);
    if (error) {
      setMessage({ type: 'error', text: 'Failed to delete sale' });
    } else {
      setMessage({ type: 'success', text: 'Sale deleted!' });
      fetchSales();
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const toggleActive = async (id, current) => {
    const { error } = await supabase
      .from('sales')
      .update({ is_active: !current, updated_at: new Date() })
      .eq('id', id);
    if (!error) fetchSales();
  };

  const handleProductToggle = (productId) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  if (loading) {
    return <div className={styles.loadingContainer}><div className={styles.loadingSpinner}></div></div>;
  }

  return (
    <div className={styles.adminSales}>
      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
          {message.text}
        </div>
      )}

      <div className={styles.header}>
        <h2>Sales Campaigns</h2>
        <button onClick={() => handleOpenModal()} className={styles.addBtn}>
          <FiPlus /> Create Sale
        </button>
      </div>

      <div className={styles.salesGrid}>
        {sales.length === 0 ? (
          <div className={styles.emptyState}>
            <FiTag className={styles.emptyIcon} />
            <h3>No Sales Yet</h3>
            <p>Create seasonal sales like Monsoon Sale, Winter Sale, or Festival Sale.</p>
            <button onClick={() => handleOpenModal()} className={styles.emptyBtn}>
              <FiPlus /> Create First Sale
            </button>
          </div>
        ) : (
          sales.map(sale => {
            const isActive = sale.is_active;
            const isExpired = new Date(sale.end_date) < new Date();
            const isFuture = new Date(sale.start_date) > new Date();
            const status = !isActive ? 'Inactive' : isExpired ? 'Expired' : isFuture ? 'Upcoming' : 'Active';

            return (
              <div key={sale.id} className={styles.saleCard}>
                <div className={styles.saleImage}>
                  <img src={sale.image_url || '/placeholder.jpg'} alt={sale.name} />
                  <div className={`${styles.statusBadge} ${styles[status.toLowerCase()]}`}>
                    {status}
                  </div>
                </div>
                <div className={styles.saleContent}>
                  <h3>{sale.name}</h3>
                  <p>{sale.description?.substring(0, 80)}...</p>
                  <div className={styles.saleDetails}>
                    <span>
                      {sale.discount_type === 'percentage' ? `${sale.discount_value}% OFF` : `₹${sale.discount_value} OFF`}
                    </span>
                    <span><FiCalendar /> {new Date(sale.start_date).toLocaleDateString()} - {new Date(sale.end_date).toLocaleDateString()}</span>
                    <span><FiTag /> {sale.products?.length || 0} products</span>
                  </div>
                  <div className={styles.saleActions}>
                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={sale.is_active}
                        onChange={() => toggleActive(sale.id, sale.is_active)}
                      />
                      <span className={styles.toggleSlider}></span>
                    </label>
                    <button onClick={() => handleOpenModal(sale)} className={styles.editBtn}>
                      <FiEdit2 />
                    </button>
                    <button onClick={() => handleDelete(sale.id)} className={styles.deleteBtn}>
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className={styles.modal} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingSale ? 'Edit Sale' : 'Create Sale'}</h3>
              <button onClick={() => setShowModal(false)} className={styles.closeBtn}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Sale Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="e.g., Monsoon Sale"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Slug (URL)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="e.g., monsoon-sale"
                  required
                />
                <small>Used for the sale page URL: /sale/monsoon-sale</small>
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  placeholder="Describe this sale campaign..."
                />
              </div>

              <div className={styles.formGroup}>
                <label>Sale Image</label>
                <div className={styles.imageUploadArea}>
                  {formData.image_url ? (
                    <div className={styles.imagePreview}>
                      <img src={formData.image_url} alt="Sale preview" />
                      <button
                        type="button"
                        className={styles.removeImageBtn}
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                      >
                        <FiX />
                      </button>
                    </div>
                  ) : (
                    <label className={styles.uploadLabel}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        style={{ display: 'none' }}
                      />
                      <div className={styles.uploadBox}>
                        {uploading ? (
                          <div className={styles.uploading}>Uploading...</div>
                        ) : (
                          <>
                            <FiUpload />
                            <span>Upload Image</span>
                            <small>PNG, JPG up to 5MB</small>
                          </>
                        )}
                      </div>
                    </label>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Discount Type *</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value }))}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Discount Value *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.target.value }))}
                    placeholder={formData.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  Active
                </label>
              </div>

              <div className={styles.formGroup}>
                <label>Products in this Sale</label>
                <div className={styles.productList}>
                  {products.map(product => (
                    <label key={product.id} className={styles.productCheckbox}>
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(product.id)}
                        onChange={() => handleProductToggle(product.id)}
                      />
                      <img src={product.image_url || '/placeholder.jpg'} alt={product.name} />
                      <span>{product.name}</span>
                      <span className={styles.productPrice}>₹{product.price}</span>
                    </label>
                  ))}
                  {products.length === 0 && (
                    <div className={styles.noProducts}>No products available. Please add products first.</div>
                  )}
                </div>
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setShowModal(false)} className={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={saving || uploading} className={styles.submitBtn}>
                  {saving ? 'Saving...' : (editingSale ? 'Update Sale' : 'Create Sale')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSales;